import { useState, useCallback } from 'react';
import { QueryKey } from 'react-query';

import { TeamClientStatusesEnum } from '../../../../../../../main/teams/teamsTypes';

import {
  UniversalFilterCompanyStatusValue,
  UniversalFilterCompanyStatusModes,
  UniversalFilterCompanyStatusFilterState,
  UniversalFilterCompanyStatusOnFilterStateChange,
  UniversalFilterCompanyStatusOnChange
} from '../../UniversalFilterCompanyStatus.types';

import { useMountEffect } from '../../../../../../../common/hooks/useMountEffect';
import { useReactQueryState } from '../../../../../../../main/common/reactQuery/hooks/useReactQueryState';

import { useUniversalFilterCompanyStatusFilterState } from '../useUniversalFilterCompanyStatusFilterState';

import {
  UniversalFilterTemplateModeOption,
  UniversalFilterTemplateOnModeChange
} from '../../../../helpers/UniversalFilterTemplate';

import { filtersKeys } from '../../../../../../../locales/keys';

const defaultMode = UniversalFilterCompanyStatusModes.IS_ANY_OF;

const modeOptions: UniversalFilterTemplateModeOption<UniversalFilterCompanyStatusModes>[] =
  [
    {
      value: UniversalFilterCompanyStatusModes.IS_ANY_OF,
      i18nText: filtersKeys.modes.isAnyOf
    },
    {
      value: UniversalFilterCompanyStatusModes.IS_NONE_OF,
      i18nText: filtersKeys.modes.isNoneOf
    }
  ];

interface UniversalFilterCompanyStatusOptions {
  filterStateCacheKey: QueryKey;
  filterValue?: UniversalFilterCompanyStatusValue;
  onChange: UniversalFilterCompanyStatusOnChange;
}

function useUniversalFilterCompanyStatus({
  filterStateCacheKey,
  filterValue,
  onChange
}: UniversalFilterCompanyStatusOptions) {
  const { value: savedState, setValue: saveState } =
    useReactQueryState<UniversalFilterCompanyStatusFilterState>(
      filterStateCacheKey
    );

  const { handleApplyFilterState, handleParseFilterValue } =
    useUniversalFilterCompanyStatusFilterState({
      onChange
    });

  const [filterState, setFilterState] =
    useState<UniversalFilterCompanyStatusFilterState>(
      savedState ||
        handleParseFilterValue(filterValue) || {
          mode: defaultMode,
          statuses: []
        }
    );

  useMountEffect(() => {
    handleApplyFilterState(filterState);
  });

  const handleFilterStateChange =
    useCallback<UniversalFilterCompanyStatusOnFilterStateChange>(
      (state) => {
        setFilterState(state);
        handleApplyFilterState(state);
        saveState(state);
      },
      [handleApplyFilterState, saveState]
    );

  const handleModeChange = useCallback<
    UniversalFilterTemplateOnModeChange<UniversalFilterCompanyStatusModes>
  >(
    (mode) => {
      handleFilterStateChange({
        mode,
        statuses: filterState.statuses || []
      });
    },
    [filterState, handleFilterStateChange]
  );

  const handleStatusesChange = useCallback<
    (statuses: TeamClientStatusesEnum[]) => void
  >(
    (statuses) => {
      handleFilterStateChange({
        mode: filterState.mode,
        statuses: statuses || []
      });
    },
    [filterState, handleFilterStateChange]
  );

  return {
    modeOptions,
    filterState,
    handleModeChange,
    handleStatusesChange
  };
}

export default useUniversalFilterCompanyStatus;
